/**
 * Scoped settings query keys — always include organizationId / branchId so cache
 * does not leak across org/branch switches (e.g. navbar logo).
 */

import type { QueryClient } from '@tanstack/react-query'

export const settingsQueryKeys = {
  branchSettingsList: (branchId: string | null | undefined) => ['branch-settings', branchId ?? ''] as const,
  branchSettingsCategories: (branchId: string | null | undefined) =>
    ['branch-settings-categories', branchId ?? ''] as const,
  branchBrandingInherited: (branchId: string | null | undefined) =>
    ['branch-settings-branding-inherited', branchId ?? ''] as const,

  organizationSettings: (organizationId: string | null | undefined) =>
    ['organization-settings', organizationId ?? ''] as const,
  organizationSettingsCategories: (organizationId: string | null | undefined) =>
    ['organization-settings-categories', organizationId ?? ''] as const,
  organizationSettingsManage: (organizationId: string | null | undefined) =>
    ['organization-settings-manage', organizationId ?? ''] as const,
  organizationBranding: (organizationId: string | null | undefined) =>
    ['organization-settings-branding', organizationId ?? ''] as const,

  platformBranding: () => ['platform-settings-branding'] as const,
  publicBranding: () => ['public-branding'] as const,
} as const

export function invalidateBranchScopedSettingsQueries(queryClient: QueryClient, branchId: string | null | undefined) {
  const id = branchId ?? ''
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.branchSettingsList(id) })
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.branchSettingsCategories(id) })
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.branchBrandingInherited(id) })
}

/** When org branding changes, branch UIs that inherit org logo must refetch. */
export function invalidateOrganizationBrandingDependents(
  queryClient: QueryClient,
  organizationId: string | null | undefined
) {
  const id = organizationId ?? ''
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.organizationSettings(id) })
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.organizationSettingsCategories(id) })
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.organizationSettingsManage(id) })
  void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.organizationBranding(id) })
  void queryClient.invalidateQueries({ queryKey: ['branch-settings-branding-inherited'], exact: false })
}
